import { ObjectConfig, ObjectState } from "@homebound/form-state";
import { ReactNode } from "react";
import { ChangeEventType, ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";

// Fields shared by every workflow, edited in step 1 ("Describe Change").
// Kept separate from the per-workflow form so switching workflow can reset
// the workflow-specific form without losing the workflow / reason / type.
export type BaseFormInput = {
  workflow?: ChangeEventWorkflow | null;
  reasonId?: string | null;
  type?: ChangeEventType | null;
};
export type BaseFormState = ObjectState<BaseFormInput>;

// A single step within a workflow. These are pure data: `render` and `isComplete`
// read the form but must NOT call hooks (all hook logic lives in `useActions`).
export type WorkflowStep<F> = {
  value: GuidedChangeEventStep;
  label: string;
  // Drives the stepper's "complete" checkmark and enables the Next button.
  isComplete: (form: ObjectState<F>) => boolean;
  // Whether the step can be navigated to. Defaults to enabled.
  disabled?: (form: ObjectState<F>) => boolean;
  render: (form: ObjectState<F>) => ReactNode;
};

export type WorkflowActionArgs<F> = { base: BaseFormState; form: ObjectState<F>; projectId: string };

export type WorkflowActions = {
  // Runs when the final "submit" button is clicked.
  onSubmit: () => Promise<void>;
  // Label for the final button. Defaults to "Create Change Event".
  submitLabel?: string;
  // Loading state for the final button.
  submitting?: boolean;
  // Optional side effects run BEFORE advancing past a given step.
  // Returning `false` blocks the advance (e.g. server-side validation failed).
  onNext?: Partial<Record<GuidedChangeEventStep, () => Promise<boolean | void>>>;
};

// The contract every workflow implements. `F` is the workflow's own form shape.
export type GuidedWorkflow<F> = {
  workflow: ChangeEventWorkflow;
  formConfig: ObjectConfig<F>;
  steps: WorkflowStep<F>[];
  // A hook, so it may call useMutation / useNavigate / useSnackbar / useModal, etc.
  // Safe to call dynamically because the runner mounts each workflow with a
  // `key={workflow}`, keeping this hook's identity stable per mounted instance.
  useActions: (args: WorkflowActionArgs<F>) => WorkflowActions;
};

// Identity helper. Infers `F` from the passed object literal so a workflow's
// `steps` and `useActions` are fully typed against its own form shape at the
// definition site, with no explicit generic needed.
export function defineWorkflow<F>(workflow: GuidedWorkflow<F>): GuidedWorkflow<F> {
  return workflow;
}
